import type { IsoDate } from '../types/domain.js';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): value is IsoDate {
  if (!ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function assertIsoDate(value: string): IsoDate {
  if (!isIsoDate(value)) throw new Error(`Not an ISO date (YYYY-MM-DD): ${value}`);
  return value;
}

/** Calendar date of an instant, in UTC. Dates in this app are never local-time. */
export function toIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

export function todayIso(now: Date = new Date()): IsoDate {
  return toIsoDate(now);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return toIsoDate(shifted);
}

/** First day of the month a date falls in — the key `budgets.month` uses. */
export function monthStart(date: IsoDate): IsoDate {
  return `${date.slice(0, 7)}-01`;
}

/** Last day of the month a date falls in. */
export function monthEnd(date: IsoDate): IsoDate {
  const start = new Date(`${monthStart(date)}T00:00:00Z`);
  start.setUTCMonth(start.getUTCMonth() + 1);
  start.setUTCDate(0);
  return toIsoDate(start);
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  const fromMs = Date.parse(`${from}T00:00:00Z`);
  const toMs = Date.parse(`${to}T00:00:00Z`);
  return Math.round((toMs - fromMs) / 86_400_000);
}

/**
 * How many days until an integration's credential dies.
 * The dashboard warns at 14 days, per the spec.
 */
export const EXPIRY_WARNING_DAYS = 14;

export function isExpiringSoon(
  expiresAt: string | null,
  now: Date = new Date(),
  withinDays: number = EXPIRY_WARNING_DAYS,
): boolean {
  if (!expiresAt) return false;
  const remaining = daysBetween(todayIso(now), toIsoDate(new Date(expiresAt)));
  return remaining <= withinDays;
}
